import React from 'react';
import { MessageSquare } from 'lucide-react';
import { STATUS_CONFIG, STATUS_CHIP_COLORS, BADGE_COLORS } from './constants';
import { formatRelative } from './utils';
import { TagChipList } from './LeadTags';
import { LeadAvatar } from './LeadAvatar';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Parte el texto en tramos marcados / no marcados para resaltar la frase
// buscada dentro del extracto del mensaje.
const splitByTerm = (text, term) => {
  const needle = (term || '').trim();
  if (!needle) return [{ text, hit: false }];
  const rx = new RegExp(`(${escapeRegExp(needle)})`, 'gi');
  return text
    .split(rx)
    .filter(part => part !== '')
    .map(part => ({ text: part, hit: part.toLowerCase() === needle.toLowerCase() }));
};

export const ChatListItem = ({ lead, onClick, isSelected, searchTerm }) => {
  const cfg = STATUS_CONFIG[lead.status] || STATUS_CONFIG.nuevo;
  const statusChip = STATUS_CHIP_COLORS[lead.status] || STATUS_CHIP_COLORS.all;
  const hasUnread = lead.unread_count > 0 || lead.has_unread_messages;
  const adBadge = lead.ad_badge;
  const adColor = adBadge ? (BADGE_COLORS[adBadge.color] || BADGE_COLORS.blue) : null;
  const lastTime = lead.last_interaction || lead.created_at;
  const relTime = formatRelative(lastTime);

  const previewText = lead.last_message_text || lead.phone;

  // Coincidencia encontrada DENTRO de la conversación (§41): mostramos el
  // extracto del mensaje con la frase resaltada.
  const msgMatch = lead.message_match;
  const matchParts = msgMatch?.snippet ? splitByTerm(msgMatch.snippet, searchTerm) : null;

  return (
    <button
      type="button"
      onClick={() => onClick(lead)}
      data-testid={`chat-list-item-${lead.id}`}
      className={`w-full flex items-center gap-3 p-3 mb-1.5 rounded-[10px] text-left transition-all cursor-pointer border border-transparent ${
        isSelected ? 'bg-emerald-500/10 border-emerald-500/40' : 'hover:bg-white/5'
      }`}
    >
      <LeadAvatar lead={lead} size="sm" ring={hasUnread} statusClass={cfg.dot} />
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[#e8ecf4] text-[13px] font-bold truncate leading-none mb-1">{lead.name || lead.phone}</span>
          <span className={`text-[10px] shrink-0 leading-none ${hasUnread ? 'text-[#00E676] font-bold' : 'text-[#8892a4]'}`}>{relTime}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[#8892a4] truncate flex-1 leading-tight mt-0.5">
            {previewText}
          </span>
          {adBadge && (
            <span className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full border ${adColor.border} ${adColor.bg} text-[9px] font-semibold ${adColor.text} max-w-[70px] truncate shrink-0`}>
              ● {adBadge.label}
            </span>
          )}
          {hasUnread && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#00E676] text-[#06210f] text-[11px] font-black shrink-0 ml-1">
              {lead.unread_count || 1}
            </span>
          )}
        </div>
        {matchParts && (
          <div className="mt-1 flex items-start gap-1" data-testid={`chat-list-match-${lead.id}`}>
            <MessageSquare className="w-3 h-3 shrink-0 mt-[1px] text-amber-400/80" />
            <span className="text-[10.5px] leading-tight text-amber-200/80 line-clamp-2">
              {matchParts.map((part, idx) => (
                part.hit
                  ? <mark key={idx} className="rounded bg-amber-400/25 px-0.5 text-amber-100">{part.text}</mark>
                  : <span key={idx}>{part.text}</span>
              ))}
            </span>
            {msgMatch.count > 1 && (
              <span className="shrink-0 rounded-full bg-amber-400/15 px-1.5 text-[9px] font-bold text-amber-300" title={`${msgMatch.count} mensajes con la frase`}>
                {msgMatch.count}
              </span>
            )}
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span
            data-testid={`chat-list-status-${lead.id}`}
            title={`Estado: ${cfg.label}`}
            className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide ${statusChip.badge}`}
          >
            <span className={`h-1 w-1 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
          <TagChipList tags={lead.tag_details} max={3} size="xs" />
        </div>
      </div>
    </button>
  );
};
